// Copyright (c) 2026 Chain Capital Technologies Ltd. All rights reserved.
// See LICENSE file in the project root for full license information.

// S06.5: main-thread proxy for the key-agent Worker.
//
// The SPA never holds a reference to a `SoftwareKeyAgent`. Instead it
// asks `createKeyAgentClient(...)` for a proxy object that implements
// the same surface asynchronously and routes every call through
// postMessage to a DedicatedWorker running the key-agent bundle.
//
// Design notes:
//   - The sync helpers on the source `SoftwareKeyAgent` (unlock, lock,
//     isUnlocked, setEmail) become async on the client. Every call
//     site in the SPA already `await`s agent methods, so the shape
//     change is surface-level.
//   - Lock/unlock events posted by the Worker (via its own
//     `dispatchAgentEvent` path) are re-fired on `window` as
//     CustomEvents so existing session-context listeners keep working
//     unchanged.
//   - Errors reconstructed client-side keep `name` so callers can
//     still `if (err instanceof SessionExpiredError)`.
//   - A short-lived client (used for the pre-unlock passcode check)
//     can be cleaned up with `.terminate()`.

import type {
  AccountWipeRequest,
  AccountWipeReport,
  KeyAgent,
  VaultKeys,
  VaultAccountXpubs,
  SharedVaultKeyContribution,
  SharedWithdrawalSignRequest,
  ActivationBundle,
  NetworkName,
  WalletSpendInput,
  WalletSpendResult,
  WalletSpendEstimate,
  AnchorHint,
  PsbtInspection,
  AppSignatureProof,
  WithdrawalAttestationCheck,
  WorkerRequest,
  WorkerOutbound,
} from '@cobault/key-agent';

// Mirror of the worker-side SessionExpiredError so callers can do
// `err instanceof SessionExpiredError` after a worker-originated
// timeout without the main thread having to load the key-agent
// source module. Name must match the worker's error exactly.
export class SessionExpiredError extends Error {
  constructor() {
    super('Session expired — unlock with passphrase to continue');
    this.name = 'SessionExpiredError';
  }
}

function reconstructError(err: { name: string; message: string }): Error {
  if (err.name === 'SessionExpiredError') return new SessionExpiredError();
  const e = new Error(err.message);
  e.name = err.name;
  return e;
}

/** The main-thread-facing surface of the Worker-backed key agent.
 *  Superset of `KeyAgent` plus lifecycle + seed helpers. All async. */
export interface KeyAgentClient extends KeyAgent {
  setEmail(email: string): Promise<void>;
  unlock(passphrase: string): Promise<void>;
  lock(): Promise<void>;
  isUnlocked(): Promise<boolean>;
  /** COB-385: refresh the agent's inactivity clock from UI activity.
   *  No-op when locked/expired — never revives a fired lock. */
  touch(): Promise<void>;
  initializeSeed(): Promise<string>;
  hasSeed(): Promise<boolean>;
  clearSeed(): Promise<void>;
  restoreSeed(mnemonic: string): Promise<void>;
  confirmBackup(): Promise<void>;
  isBackupConfirmed(): Promise<boolean>;
  /** COB-500: wipe this account's local state from this browser. The agent
   *  deletes only what the request names plus the master rows it can prove
   *  are this account's; everything else in the shared object store — other
   *  accounts' seeds, anchors, index entries, and `nextIndex` — survives.
   *  Implemented by all three backends (see `AccountWiper`). */
  wipeAccount(request: AccountWipeRequest): Promise<AccountWipeReport>;
  /** F12: passcode verifier derivation inside the Worker. */
  deriveVerifier(
    email: string,
    passcode: string,
    publicSalt: Uint8Array,
  ): Promise<string>;
  /** Inject the Web3Auth-reconstructed secp256k1 key (hex) into the
   *  Worker. Only valid when the client was created with
   *  agentType 'web3auth'. */
  loadWeb3AuthKey(privateKeyHex: string): Promise<void>;
  /** Tear down the Worker and reject all pending calls. Safe to call
   *  multiple times. */
  terminate(): void;
}

/**
 * Construct a new Worker-backed key-agent client. Each call creates a
 * fresh Worker with its own SoftwareKeyAgent; callers should share a
 * single client across the session and only spin up a short-lived
 * client for flows that don't need the full session (e.g. the pre-
 * unlock passcode-check).
 */
/** Which backend the Worker should construct on `init`. */
export type KeyAgentType = 'software' | 'web3auth';

export function createKeyAgentClient(
  network: NetworkName = 'regtest',
  timeoutMs?: number,
  agentType: KeyAgentType = 'software',
): KeyAgentClient {
  const worker = new Worker('/key-agent-worker.js', { type: 'module' });
  return wrapWorker(worker, network, timeoutMs, agentType);
}

/**
 * Low-level constructor used by tests: accepts an already-constructed
 * Worker (or a MessagePort-like fake). Real production callers should
 * always go through `createKeyAgentClient`.
 */
export function wrapWorker(
  worker: Worker,
  network: NetworkName,
  timeoutMs?: number,
  agentType: KeyAgentType = 'software',
): KeyAgentClient {
  const pending = new Map<number, { resolve: (v: unknown) => void; reject: (e: Error) => void }>();
  let nextId = 1;
  let terminated = false;

  worker.onmessage = (ev: MessageEvent<WorkerOutbound>) => {
    const msg = ev.data;
    if ('type' in msg) {
      if (typeof window !== 'undefined' && typeof CustomEvent !== 'undefined') {
        window.dispatchEvent(new CustomEvent(msg.name));
      }
      return;
    }
    const entry = pending.get(msg.id);
    if (!entry) return;
    pending.delete(msg.id);
    if (msg.ok) {
      entry.resolve(msg.result);
    } else {
      entry.reject(reconstructError(msg.error));
    }
  };

  worker.onerror = (ev) => {
    const err = new Error(`Worker error: ${ev.message ?? 'unknown'}`);
    for (const entry of pending.values()) entry.reject(err);
    pending.clear();
  };

  function call<T>(method: string, args: readonly unknown[]): Promise<T> {
    if (terminated) return Promise.reject(new Error('Worker terminated'));
    const id = nextId++;
    const req: WorkerRequest = { id, method, args };
    return new Promise<T>((resolve, reject) => {
      pending.set(id, {
        resolve: (v) => resolve(v as T),
        reject,
      });
      worker.postMessage(req);
    });
  }

  // Fire-and-forget init; every other call awaits it implicitly because
  // the Worker processes messages strictly in order. Returning nothing
  // here keeps the constructor synchronous for callers.
  void call<void>('init', [network, timeoutMs, agentType]);

  return {
    setEmail: (email) => call<void>('setEmail', [email]),
    unlock: (passphrase) => call<void>('unlock', [passphrase]),
    lock: () => call<void>('lock', []),
    isUnlocked: () => call<boolean>('isUnlocked', []),
    touch: () => call<void>('touch', []),
    initializeSeed: () => call<string>('initializeSeed', []),
    hasSeed: () => call<boolean>('hasSeed', []),
    clearSeed: () => call<void>('clearSeed', []),
    restoreSeed: (mnemonic) => call<void>('restoreSeed', [mnemonic]),
    confirmBackup: () => call<void>('confirmBackup', []),
    isBackupConfirmed: () => call<boolean>('isBackupConfirmed', []),
    // COB-500: plain strings across the boundary; every guard lives in the
    // Worker-side agent, never here.
    wipeAccount: (request: AccountWipeRequest) =>
      call<AccountWipeReport>('wipeAccount', [request]),
    generateKey: (vaultId) => call<VaultKeys>('generateKey', [vaultId]),
    getPubkey: (vaultId) => call<string>('getPubkey', [vaultId]),
    exportAccountXpubs: (vaultId, derivationIndexHint) =>
      call<VaultAccountXpubs>('exportAccountXpubs', [vaultId, derivationIndexHint]),
    contributeSharedVaultKey: (vaultId, slotId) =>
      call<SharedVaultKeyContribution>('contributeSharedVaultKey', [
        vaultId,
        slotId,
      ]),
    acknowledgeActivation: (vaultId, bundle: ActivationBundle, derivationIndexHint) =>
      call<AppSignatureProof>('acknowledgeActivation', [
        vaultId,
        bundle,
        derivationIndexHint,
      ]),
    signSharedWithdrawalIntent: (vaultId, bundle: ActivationBundle, outputs, derivationIndexHint) =>
      call<AppSignatureProof>('signSharedWithdrawalIntent', [
        vaultId,
        bundle,
        outputs,
        derivationIndexHint,
      ]),
    partialSignSharedWithdrawal: (
      vaultId,
      req: SharedWithdrawalSignRequest,
      derivationIndexHint,
    ) =>
      call<Uint8Array>('partialSignSharedWithdrawal', [
        vaultId,
        req,
        derivationIndexHint,
      ]),
    // COB-491 (R5): `attestation` rides the same RPC. Plain JSON + three
    // strings, so structured clone carries it verbatim; the Worker-side agent
    // is what refuses when it is missing or does not verify.
    signPsbt: (vaultId, psbt, derivationIndexHint, anchorHint, attestation) =>
      call<Uint8Array>('signPsbt', [
        vaultId,
        psbt,
        derivationIndexHint,
        anchorHint,
        attestation,
      ]),
    checkWithdrawalAttestation: (vaultId, psbt, attestation) =>
      call<WithdrawalAttestationCheck>('checkWithdrawalAttestation', [
        vaultId,
        psbt,
        attestation,
      ]),
    sealVaultAnchor: (vaultId, hint: AnchorHint) =>
      call<void>('sealVaultAnchor', [vaultId, hint]),
    inspectPsbt: (psbt) => call<PsbtInspection>('inspectPsbt', [psbt]),
    exportSeed: () => call<string>('exportSeed', []),
    getIdentityPubkey: () => call<string>('getIdentityPubkey', []),
    signChallenge: (challenge) =>
      call<AppSignatureProof>('signChallenge', [challenge]),
    signOwnershipChallenge: (vaultId, nonceHex, derivationIndexHint) =>
      call<string>('signOwnershipChallenge', [
        vaultId,
        nonceHex,
        derivationIndexHint,
      ]),
    signOwnershipChallengeProof: (vaultId, nonceHex, derivationIndexHint) =>
      call<AppSignatureProof>('signOwnershipChallengeProof', [
        vaultId,
        nonceHex,
        derivationIndexHint,
      ]),
    // COB-507: the verifier-shared participant proof. The roster is passed as
    // a plain array (structured-clone-safe); `undefined` selects the weak
    // index ladder inside the agent.
    signSharedVaultOwnershipProof: (
      vaultId,
      nonceHex,
      roster,
      derivationIndexHint,
    ) =>
      call<AppSignatureProof>('signSharedVaultOwnershipProof', [
        vaultId,
        nonceHex,
        roster === undefined ? roster : [...roster],
        derivationIndexHint,
      ]),
    getWalletAddress: () => call<string>('getWalletAddress', []),
    buildAndSignWalletSpend: (input: WalletSpendInput) =>
      call<WalletSpendResult>('buildAndSignWalletSpend', [input]),
    // COB-485 Ticket B: pricing a send crosses the same boundary as building
    // one, so the estimate the user reads and the fee the transaction pays
    // come out of one implementation.
    estimateWalletSpend: (input: WalletSpendInput) =>
      call<WalletSpendEstimate>('estimateWalletSpend', [input]),
    deriveVerifier: (email, passcode, publicSalt) =>
      call<string>('deriveVerifier', [email, passcode, publicSalt]),
    loadWeb3AuthKey: (privateKeyHex) =>
      call<void>('loadKey', [privateKeyHex]),
    terminate: () => {
      if (terminated) return;
      terminated = true;
      for (const entry of pending.values()) {
        entry.reject(new Error('Worker terminated'));
      }
      pending.clear();
      worker.terminate();
    },
  };
}
